import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { KeyRound, Copy, Loader2, ShieldCheck } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Modal do super-admin: lista os usuarios de uma empresa cliente e permite
// resetar a senha de cada um. A senha nova aparece UMA vez, para copiar e
// repassar ao cliente — nao fica guardada em lugar nenhum legivel.
const CompanyUsersModal = ({ open, onClose, company }) => {
	const [usuarios, setUsuarios] = useState([]);
	const [carregando, setCarregando] = useState(false);
	const [resetandoId, setResetandoId] = useState(null);
	// { userId, name, email, novaSenha } da ultima senha gerada, para exibir.
	const [senhaGerada, setSenhaGerada] = useState(null);

	const carregar = useCallback(async () => {
		if (!company?.id) return;
		setCarregando(true);
		setSenhaGerada(null);
		try {
			const { data } = await api.get(`/saas/companies/${company.id}/users`);
			setUsuarios(data || []);
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, [company]);

	useEffect(() => {
		if (open) carregar();
	}, [open, carregar]);

	const resetar = async (usuario) => {
		setResetandoId(usuario.id);
		try {
			const { data } = await api.post(
				`/saas/companies/${company.id}/users/${usuario.id}/reset-password`,
			);
			setSenhaGerada(data);
			toast.success(`Senha de ${usuario.name} redefinida.`);
		} catch (err) {
			toastError(err);
		}
		setResetandoId(null);
	};

	const copiar = async (texto) => {
		try {
			await navigator.clipboard.writeText(texto);
			toast.success("Senha copiada.");
		} catch {
			toast.error("Não consegui copiar. Selecione e copie na mão.");
		}
	};

	return (
		<Dialog open={open} onOpenChange={(aberto) => !aberto && onClose()}>
			<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>
						Usuários — {company?.name || "empresa"}
					</DialogTitle>
				</DialogHeader>

				{senhaGerada && (
					<div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 dark:border-emerald-800 dark:bg-emerald-950">
						<div className="mb-1 flex items-center gap-1.5 text-sm font-medium text-emerald-800 dark:text-emerald-300">
							<ShieldCheck className="h-4 w-4" />
							Nova senha de {senhaGerada.name}
						</div>
						<div className="flex items-center gap-2">
							<code className="flex-1 rounded bg-background px-2 py-1 font-mono text-sm">
								{senhaGerada.novaSenha}
							</code>
							<Button
								variant="outline"
								size="icon"
								className="h-8 w-8"
								aria-label="Copiar senha"
								onClick={() => copiar(senhaGerada.novaSenha)}
							>
								<Copy className="h-4 w-4" />
							</Button>
						</div>
						<p className="mt-1.5 text-xs text-muted-foreground">
							Copie e repasse ao cliente agora — ela não fica guardada.
						</p>
					</div>
				)}

				{carregando ? (
					<div className="flex items-center justify-center py-8 text-muted-foreground">
						<Loader2 className="h-5 w-5 animate-spin" />
					</div>
				) : usuarios.length === 0 ? (
					<p className="py-8 text-center text-sm text-muted-foreground">
						Esta empresa não tem usuários cadastrados.
					</p>
				) : (
					<div className="flex flex-col divide-y">
						{usuarios.map((u) => (
							<div
								key={u.id}
								className="flex items-center justify-between gap-3 py-2.5"
							>
								<div className="min-w-0">
									<div className="truncate text-sm font-medium">
										{u.name}
										{u.profile === "admin" && (
											<span className="ml-1.5 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
												admin
											</span>
										)}
									</div>
									<div className="truncate text-xs text-muted-foreground">
										{u.email}
									</div>
								</div>
								<Button
									variant="outline"
									size="sm"
									disabled={resetandoId === u.id}
									onClick={() => resetar(u)}
								>
									{resetandoId === u.id ? (
										<Loader2 className="h-4 w-4 animate-spin" />
									) : (
										<>
											<KeyRound className="mr-1.5 h-3.5 w-3.5" />
											Resetar senha
										</>
									)}
								</Button>
							</div>
						))}
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default CompanyUsersModal;
